/**
 * Estrutura de suporte técnico por empresa (GUPM-200) — display do V8
 * (`SUPORTE_TECNICO`, 1759-1803): empresa / papel / escopo / cor de marca.
 *
 * SEM PII: aqui é só o CABEÇALHO de cada empresa. As PESSOAS (nome/telefone/
 * e-mail) vêm do banco `contacts` (contatos com empresa, GUPM-202) via datasource —
 * nunca versionadas (LGPD/GUPM-158). O seed dos contatos placeholder (papéis +
 * UUID fixo, sem PII) está em `@/domain/contatos/suporte-tecnico-seed`.
 * Travado pelo teste `suporte-seed-sem-pii-versionada`.
 */

export interface SuporteEmpresa {
  empresa: string;
  papel: string;
  /** Cor de marca (display). */
  cor: string;
  escopo: string;
  /**
   * Seção que saiu da Matriz RACI Rev.1 (18/10/2026) mas fica no código para voltar se
   * precisar. Só aparece se tiver contato ATIVO — ver `empresasVisiveis`.
   */
  arquivada?: true;
}

/** 7 empresas do V8, na ordem, + os órgãos públicos (GUPM-202). Só estrutura — zero PII. */
export const SUPORTE_TECNICO: readonly SuporteEmpresa[] = [
  {
    empresa: 'JF Brasil Engenharia',
    papel: 'Desenvolvedor e suporte do sistema GeoGuard',
    cor: '#FECB00',
    escopo: 'Manutenção e suporte do software GeoGuard; diagnóstico técnico (horas mensais mediante solicitação do RT).',
  },
  {
    empresa: 'Foscentro (UPM)',
    papel: 'Empreendedor — Responsável Técnico e operação da UPM',
    cor: '#78B4CF',
    escopo: 'Responsável técnico pela barragem; releitura remota; direciona diagnóstico técnico; dá baixa em TARP; enquadra nível ANM.',
  },
  {
    empresa: 'Walm (EoR)',
    papel: 'Engenheiro of Record — Especialista de Regime',
    cor: '#A6CE3A',
    escopo: 'Consulta técnica obrigatória a partir do Nível 2 (Alerta). Cartas de controle e análise de estabilidade.',
  },
  {
    empresa: 'TetraTech',
    papel: 'Manutenção da instrumentação geotécnica',
    cor: '#E4A944',
    escopo: 'Manutenção preventiva/corretiva dos instrumentos; verificação de integridade e releitura manual. Acionada em perda de sinal de instrumento (até 48h).',
  },
  {
    empresa: 'TECAL',
    papel: 'Manutenção de câmeras e sirenes',
    cor: '#93A1C7',
    escopo: 'Mantém câmeras (visão noturna, retenção ≥90 dias) e sirenes (incl. acionamento automático pelos tiltímetros). Acionada em perda de sinal de câmeras/sirenes.',
  },
  {
    empresa: 'DF+ (CMG — em transição)',
    papel: 'Operação atual do CMG — JF Brasil assumirá o CMG',
    cor: '#8B95A7',
    escopo: 'Operação atual do Centro de Monitoramento Geotécnico; em processo de substituição pelo GeoGuard/JF Brasil. Gestão de mudança em curso.',
    // RACI Rev.1, nota 1: transição do CMG concluída, DF+ retirada da matriz.
    arquivada: true,
  },
  {
    empresa: 'TI Foscentro',
    papel: 'Infraestrutura de TI — internet, energia e redundâncias do CMG',
    cor: '#6B7280',
    escopo: 'Fornece e mantém infraestrutura de TI do CMG: internet, energia e redundâncias (elétrica e de comunicação). Comissionamento de equipamentos junto à JF Brasil.',
    // O responsável de TI é da Foscentro e fica na seção "Foscentro (UPM)" (18/10/2026).
    arquivada: true,
  },
  // GUPM-202 (D-10): os órgãos da cadeia de emergência também aparecem no diretório.
  {
    empresa: 'Órgãos públicos',
    papel: 'Defesa Civil, ANM, Bombeiros, Polícia Militar e FEAM',
    cor: '#EF4444',
    escopo: 'Órgãos acionados pelo PAEBM em emergência. Telefones institucionais públicos.',
  },
] as const;

/**
 * Seções da tela de suporte, na ordem de `SUPORTE_TECNICO`. A arquivada só entra se tiver
 * contato ativo: com contato ativo nela, escondê-la tiraria uma pessoa da tela sem aviso.
 */
export function empresasVisiveis(
  contatos: readonly { empresa: string | null; is_active: boolean }[],
): SuporteEmpresa[] {
  const comAtivo = new Set(contatos.filter((c) => c.is_active).map((c) => c.empresa));
  return SUPORTE_TECNICO.filter((e) => !e.arquivada || comAtivo.has(e.empresa));
}
